import * as THREE from 'three';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import TWEEN from '@tweenjs/tween.js';
import { KTX2Loader } from 'three/addons';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls'
import CameraControls from 'camera-controls';
import ScrollToAnimateControls from '../three-controls/ScrollToAnimateControls';
import {
	SCROLL_ANIMATION_DEFAULT_NAME,
	SCROLL_ANIMATION_DEFAULT_NAME_DESKTOP,
	SCROLL_ANIMATION_DEFAULT_NAME_MOBILE,
} from '../three-controls/constants.js';

import { isMobile } from 'react-device-detect';
import { applyLightMaps } from '../utils/textureHelpers';

export default class GLB_object extends THREE.Group {
	#loader = new GLTFLoader();
	constructor({
		url,
		is_animation_on,
		onLoad = () => {},
		scene,
		renderer,
		camera,
		setCurrentCamera,
		isScrollToMove,
		orbitControlsConfig,
		...restConfig
	}) {
		super();
		this.scene = scene;
		this.name = restConfig?.name; //IMPORTANT for placing hotspots in the nested worlds!!!!
		this.clock = new THREE.Clock();
		this.renderer = renderer;
		this.setCurrentCamera = setCurrentCamera;
		this.transformControl = null
		this.camera = camera;
		this.config = {
			url,
			is_animation_on,
			isScrollToMove,
			...restConfig,
			environment_map_intensity:
				restConfig?.environment_map_intensity || 1,
			orbitControlsConfig: orbitControlsConfig || {},
		}; //no need to extract every property from constructor prop
		// this.dracoLoaderManager = new DracoManager();

		//Scene model doesn't use transforms, so for this case we support this props here:scale, position,
		if (this.config?.scale) this.setScale(this.config.scale);
		if (this.config?.position) this.setPosition(this.config.position);

		//Loader
		if (url)
			this.loadModel(url)
				.then(() => {
					if(this.config?.data?.enableRotationAxes){
						this.enableDragRotation(this.config?.data?.enableRotationAxes)
					}
					onLoad(this)
				})
	}

	// enable drag rotation axes
	enableDragRotation = (config)=>{
		if(!this.glbModel) return
		// if not transformcontrol create one
		if(!this.transformControl){
			this.transformControl = new TransformControls(this.config.controls.camera, this.renderer.domElement)
			this.transformControl.addEventListener('mouseDown',  (e) =>{

				this.config.controls.enabled = false
			})

			this.transformControl.addEventListener('mouseUp',  () =>{
				this.config.controls.enabled = true
			})
			this.transformControl.setMode('rotate')
			const gizmo = this.transformControl.getHelper();
			this.scene.add( gizmo );
		}
			// hide gizmo
			this.transformControl._gizmo.visible = false
			// add object if there none
			if (!this.transformControl.object){
				this.transformControl.attach(this.glbModel)
			}
			// enable axes  requested
			this.transformControl._gizmo.showX = config?.x ? true : false
			this.transformControl._gizmo.showY = config?.y? true: false
			this.transformControl._gizmo.showZ = config?.z ? true: false
			if(config.size){
				this.transformControl.size = config.size
			}
			if(config.position){
				if (config?.position?.x) this.transformControl._gizmo.position.x = config.position.x
				if (config?.position?.y) this.transformControl._gizmo.position.y = config.position.y
				if (config?.position?.z) this.transformControl._gizmo.position.x = config.position.z
			}
			// reset rotation
			if(this.glbModel) {
				this.glbModel.rotation.set(
					0, 0, 0
				);
			}
	}
	//AVA ***
	updateConfig = (newConfig) => {
		// console.log('%c GLB update ', 'color:blue', { newConfig });
		// if (newConfig.url !== this.config.url) this.loadModel(newConfig.url);
	};

	// set rotation of model by sending xyz
	setRotation = (rotation) => {
		this.rotation.set(
			rotation?.x || this.rotation._x,
			rotation?.y || this.rotation._y,
			rotation?.z || this.rotation._z,
		);
	};
	setPosition(coord) {
		this.config.position = coord;
		this.position.set(coord?.x || 0, coord?.y || 0, coord?.z || 0);
	}

	setScale(scale) {
		// console.log('-setScale', scale);
		this.config.scale = scale;
		this.scale.set(scale, scale, scale);
	}

	saveScrollAnimationNamesToConfig = (animations) => {
		if (!animations || animations.length === 0) return;
		const { scrollAnimationNameMobile, scrollAnimationNameDesktop } =
			this.config.orbitControlsConfig?.on_rails_config || {};

		const mobileName =
			scrollAnimationNameMobile ||
			SCROLL_ANIMATION_DEFAULT_NAME_MOBILE;
		const desktopName =
			scrollAnimationNameDesktop ||
			SCROLL_ANIMATION_DEFAULT_NAME_DESKTOP;

		const deviceSpecificAnimationName = isMobile ? mobileName : desktopName;

		this.config.scrollAnimation =
			animations.find((anim) => anim.name === deviceSpecificAnimationName) ||
			animations.find((anim) => anim.name === SCROLL_ANIMATION_DEFAULT_NAME);

		if (!this.config.scrollAnimation) {
			console.warn(`[GLB_object] No scroll animation found for: ${deviceSpecificAnimationName} or ${SCROLL_ANIMATION_DEFAULT_NAME}`);
		}

		this.config.scrollAnimationNames = Array.from(
			new Set([
				SCROLL_ANIMATION_DEFAULT_NAME,
				mobileName,
				desktopName,
			])
		);
	};

	loadModel = async (url) => {
		if (!url) return;

		// this.dracoLoaderManager.setActive();
		this.dracoLoader = new DRACOLoader();

		this.dracoLoader.setDecoderPath(
			'https://www.gstatic.com/draco/versioned/decoders/1.5.6/',
		);
		this.#loader.setDRACOLoader(this.dracoLoader);

		// TODO: This is a temp condition to identify ktx compressed GLB. Replace with a capabilities object.
		if (url.includes('_ktx2_')) {
			this.ktxLoader = new KTX2Loader();
			this.ktxLoader.setTranscoderPath(
				'https://cdn.obsess-vr.com/ktx2Transcoder/',
			);
			this.ktxLoader.detectSupport(this.renderer);
			this.#loader.setKTX2Loader(this.ktxLoader);
		}

		return this.#loader
			.loadAsync(url)
			.then(async (model) => {
				this.model = model;
				this.glbModel = model.scene;
				try {
					await applyLightMaps(model.scene);
				} catch (error) {
					console.warn(
						'Failed to apply lightmaps, continuing without them:',
						error.message,
					);
				}
				this.glbModel.animations.push(...model.animations);
				this.glbModel.name = url.split('/').pop();
				this.glbModel._type = this.config?._type;
				this.mixer = new THREE.AnimationMixer(this.glbModel);
				if (this.config.isScrollToMove && this.glbModel?.animations?.length > 0) {
					// save scroll animation names to config
					this.saveScrollAnimationNamesToConfig(this.glbModel.animations);
					
					if (this.config.scrollAnimation) {
						this.scrollToAnimateControls =
							new ScrollToAnimateControls(
								this.mixer,
								this.renderer,
								model,
								this.config.scrollAnimation,
								this.camera,
								this.setCurrentCamera,
								this.scene,
								this.config.orbitControlsConfig?.on_rails_config,
							);
					}
				}

				/** Env Map **/
				if (this.config.environment_map_intensity)
					this.setEnvMapIntensity(
						this.config.environment_map_intensity,
					);

				/** KHR Materials **/
				if (
					model?.userData?.gltfExtensions?.['KHR_materials_variants']
				) {
					this.KHR_materials_variants =
						model?.userData?.gltfExtensions?.[
							'KHR_materials_variants'
						];
					if (this.config.khr_material_variant)
						await this.set_KHR_Variant(
							this.config.khr_material_variant,
						);
				}


				// Hide meshes by default
				this.glbModel.traverse((child) => {
					if (child.name.endsWith('default_hidden')) {
						child.visible = false;
					}
					if (child.name === 'collision_mesh') {
						child.visible = false;
					}
				});

				/** Colored materials Materials (designer mark some object meshes) **/ /** Skip for XR for now */
				if (!this.config.isXREnabled) this.useCustomColorsOnMeshes();

				this.add(this.glbModel);
				if (this.glbModel?.animations?.length > 0) this.animateGLB();
				if (this.config?.data?.meshesToHide)
					this.hideMeshes(this.config?.data?.meshesToHide);
				if (this.config?.data?.applyTexturesToMeshes)
					this.applyTexturesToMeshes(
						this.config?.data?.applyTexturesToMeshes,
					);

				// this?.ktxLoader && this.ktxLoader.dispose();
				return this;
			})
			.catch((err) => {
				console.error('Err loadModel()', { url, err });
			})
			.finally(() => {
				// this.dracoLoaderManager.dispose();
				// this.dracoLoader.dispose();
			});
	};

	setEnvMapIntensity = (val) => {
		if (!val) return;
		// console.log('setEnvMapIntensity', val);
		this.config.environment_map_intensity = val; //update config

		if (val >= 0 && this?.glbModel) {
			this.glbModel?.traverse((child) => {
				if (
					child instanceof THREE.Mesh &&
					child.material instanceof THREE.MeshStandardMaterial
				) {
					child.material.envMapIntensity = val;
					child.material.needsUpdate = true;
				}
			});
		}
	};

	//if designer mark object(s) as object.userData.use_primary_color,
	//we should apply store.primary_color on that object
	useCustomColorsOnMeshes = () => {
		// console.log('---useCustomColorsOnMeshes', this.config)
		const { primary_color, secondary_color } = this.config;
		if (!primary_color && !secondary_color) return;

		this.glbModel.traverse(async (object) => {
			if (!object?.isMesh || !object?.userData) return;

			if (object.userData?.use_primary_color && primary_color)
				object.material.color.set(primary_color);
			else if (object.userData?.use_secondary_color && secondary_color)
				object.material.color.set(secondary_color);
		});
	};

	hideMeshes = (meshes) => {
		if (!this.glbModel) return;
		if (!meshes) return;
		this.glbModel.traverse((obj) => {
			if (!obj?.isMesh) return;
			obj.visible = !meshes?.[obj.name];
		});
	};

	applyTexturesToMeshes = (textureMeshMap) => {
		if (!textureMeshMap) return;
		if (!this.glbModel) return;
		Object.keys(textureMeshMap).map((key) => {
			const mesh = this.glbModel.getObjectByName(key);
			if (mesh) {
				new THREE.TextureLoader().load(
					textureMeshMap[key].url,
					(texture) => {
						const imageRatio =
							texture.image.width / texture.image.height;
						const width = textureMeshMap[key]?.width || 1;
						const height = width / imageRatio;
						const imagePlane = new THREE.PlaneGeometry(
							width,
							height,
						);
						mesh.geometry = imagePlane;
						mesh.material.metalness = 0;
						mesh.material.map = texture;
						mesh.material.transparent = true;
						mesh.material.needsUpdate = true;
						mesh.needsUpdate = true;
					},
				);
			}
		});
	};

	set_KHR_Variant = async (variant, reset = true) => {
		if (!this.glbModel || !variant || !this?.model?.parser) return;
		// console.log('-- set_KHR_Variant', {variant});

		const variants = this.KHR_materials_variants?.variants?.map(
			(i) => i.name,
		);
		if (variants?.length < 1) {
			console.error('No KHR variants found', { variant, variants });
			return;
		}

		return new Promise((resolve) => {
			this.config.khr_material_variant = variant;
			const variantIndex = variants.findIndex((v) => v === variant);

			if (variantIndex >= 0) {
				this.glbModel.traverse(async (object) => {
					if (!object.isMesh || !object.userData?.gltfExtensions)
						return;

					const KHR_materials_variants =
						object.userData?.gltfExtensions?.[
							'KHR_materials_variants'
						];
					const khr_mapping = KHR_materials_variants?.mappings;

					//if child has KHR texture mapping
					if (khr_mapping) {
						if (!object.userData.originalMaterial)
							object.userData.originalMaterial = object.material;
						const mapping = khr_mapping.find((i) =>
							i.variants.includes(variantIndex),
						);

						if (mapping) {
							object.material =
								await this.model.parser.getDependency(
									'material',
									mapping.material,
								);
							this.model.parser.assignFinalMaterial(object);
						} else {
							if (reset) {
								object.material =
									object.userData.originalMaterial;
							}
						}
					}
				});
			} else {
				console.warn('variant not found in the variants list');
			}

			// console.log('%c > set_KHR_Variant', 'color:blue', {variant, variants: JSON.parse(JSON.stringify(variants))});
			resolve(true);
		});
	};

	animateGLB() {
		// we need an animation loop if glb has any animations
		this.id_ = Math.random();
		this.scene.addAnimationHook('GLBobject-' + this.uuid, this.animate);
		
		// filter out the scroll to move animation
		if (this.config.isScrollToMove) {
			this.animations = this.glbModel.animations.filter(
				(i) => !this.config.scrollAnimationNames.includes(i.name),
			);
			if (this.animations.length == 0) return;
		}

		this.currentAnimation = this.animations[0];
		if (this.config.is_animation_on) this.startAnimation();
	}

	startAnimation = () => {
		if (this.animations)
			this.mixer.clipAction(this.currentAnimation).play();
	};

	stopAnimation = () => {
		if (this.animations)
			this.mixer?.clipAction(this.currentAnimation).stop();
	};

	animate = (delta) => {
		// requestAnimationFrame(this.animate);
		// const delta = this.clock.getDelta();
		this.mixer.update(delta);
	};

	zoomToMesh = ({ meshName, fov, closer = 0.2, tx = 0, ty = 0 }) => {
		this.config.controls.camera.updateProjectionMatrix();
		let fovValue = { value: this.config.controls.camera.fov };

		const tween = new TWEEN.Tween(fovValue)
		.to({ value: fov }, 1000)
		.start()
		.easing(TWEEN.Easing.Cubic.Out)
		.onUpdate(() => {
			this.config.controls.camera.fov = fovValue.value;
			this.config.controls.camera.updateProjectionMatrix();
		});

		const targetObject = this.glbModel.getObjectByName(meshName);
		if (targetObject) {

			// Compute bounding sphere for targetObject
			const sphere = new THREE.Sphere();
			CameraControls.createBoundingSphere(targetObject, sphere);

			// Get the direction vector from targetObject sphere to camera
			const cameraPos = this.config.controls.camera.position;
			const normal = new THREE.Vector3()
			.subVectors(cameraPos, sphere.center)
			.normalize();

			// Get distance between camera and target Object
			const distanceBetweenCamera = cameraPos.distanceTo(sphere.center);

			// Compute a point `closer` times distance between camera. `closer` can be passed as argument and is used to move the camera `closer` times away from the targetObject.
			normal.multiplyScalar(distanceBetweenCamera * closer);

			// New Camera Position object
			const newCameraPos = new THREE.Vector3()
			.copy(sphere.center)
			.add(normal);

			// Move camera closer to the object
			this.config.controls.moveTo(
				newCameraPos.x,
				newCameraPos.y,
				newCameraPos.z,
				true,
			);

			// Move camera in XY plane to offset the camera in X or Y axes as passed in `tx` and `ty` arguments
			this.config.controls.truck(tx, ty, true);
		}
	};

	updateGLB = (url, data) => {
		this.dispose();
		// update config data to run  hideMeshes and applyTexturesToMeshes funcs with fresh data
		if (data) {
			this.config.data = data;
		}
		this.loadModel(url).then(() => {
			if(this.config?.data?.enableRotationAxes){
				this.enableDragRotation(this.config?.data?.enableRotationAxes)
			}
		})
	};

	dispose = () => {
		if (this?.scrollToAnimateControls) {
			this.scrollToAnimateControls.dispose();
		}
		if(this.transformControl){
			this.transformControl.detach(this.glbModel)
		}
		const allObjs = [];
		this.glbModel?.traverse((i) => allObjs.push(i));

		const geometries = allObjs
			.filter((i) => i.geometry)
			.map((i) => i.geometry);
		const materials = allObjs
			.filter((i) => i.material)
			.map((i) => i.material);

		const textures = [];
		const textureKeys = [
			'map',
			'lightMap',
			'bumpMap',
			'normalMap',
			'specularMap',
			'envMap',
			'alphaMap',
			'aoMap',
			'displacementMap',
			'emissiveMap',
			'gradientMap',
			'metalnessMap',
			'roughnessMap',
		];

		materials.forEach((material) => {
			textureKeys.forEach((key) => {
				if (material[key]) {
					textures.push(material[key]);
				}
			});
		});

		geometries.forEach((i) => i.dispose());

		textures.forEach((i) => {
			if (i.source?.data?.close) {
				i.source.data.close();
			}
			i.dispose();
		});

		materials.forEach((i) => i.dispose());

		this.children?.forEach((child) => {
			child.geometry?.dispose();
			child.material?.dispose();
			this.remove(child);
		});
		this.children = [];
		this.glbModel = null;
		this?.scene?.removeAnimationHook('GLBobject-' + this.uuid);
		this?.dracoLoader?.dispose();
	};
}
